import type { QueryClient } from "@tanstack/react-query";
import { getMakeupSessionsQueryKey } from "@/features/makeups/hooks/makeup-sessions-query-key";
import { parseMakeupRequestsPageParams } from "@/features/makeups/admin/makeupRequestsPageUrl";
import { resolveMakeupQueueFilters } from "@/lib/makeups/makeup-queue-view";
import {
  buildMakeupStudentNameSearchQuery,
  makeupRequestFiltersToApi,
} from "@/features/makeups/shared";
import { fetchAllMatchingStudentIds } from "@/features/students/shared/fetchAllMatchingStudentIds";
import {
  MAKEUP_SESSIONS_PAGE_SIZE,
  searchMakeupSessions,
} from "@/lib/services/makeupService";
import { QueryKeys } from "@/lib/types";
import type { AppUser } from "@/lib/types/next-auth";
import { UserRoles } from "@/lib/types/user-roles";
import { resolveEffectiveTenantNumber } from "@/lib/utils/env/effective-tenant";
import { fetchMakeupsEligibilitySettings } from "@/lib/utils/makeup-settings/makeup-settings-api";
import { getActiveRole } from "@/lib/utils/roles";

function toUrlSearchParams(
  record: Record<string, string | undefined>,
): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(record)) {
    if (value) params.set(key, value);
  }
  return params;
}

function parsePage(record: Record<string, string | undefined>): number {
  const parsed = record.page != null ? Number.parseInt(record.page, 10) : 1;
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
}

export async function prefetchAdminMakeupRequests(
  queryClient: QueryClient,
  accessToken: string,
  user: AppUser | undefined,
  searchParams: Record<string, string | undefined> = {},
) {
  const urlSearchParams = toUrlSearchParams(searchParams);
  const page = parsePage(searchParams);
  const lastSelectedId =
    page > 1 ? searchParams.lastSelectedId : undefined;
  const pageState = parseMakeupRequestsPageParams(urlSearchParams);
  const activeRole = getActiveRole(user ?? null);
  const teacherId =
    activeRole === UserRoles.Teacher && user?.id ? Number(user.id) : null;

  let resolvedStudentId = pageState.filters.studentId;
  const nameQuery = buildMakeupStudentNameSearchQuery(
    pageState.quickFirstName,
    pageState.quickLastName,
    pageState.filters,
  );
  if (nameQuery && resolvedStudentId == null) {
    const ids = await fetchAllMatchingStudentIds({
      accessToken,
      active: true,
      searchQueryRequest: nameQuery,
    });
    if (ids.length === 1) {
      resolvedStudentId = ids[0];
    }
  }

  const apiFilters = makeupRequestFiltersToApi(
    resolveMakeupQueueFilters(pageState.view, pageState.filters),
    teacherId,
    resolvedStudentId,
  );
  const tenantId = resolveEffectiveTenantNumber();

  await Promise.all([
    queryClient.prefetchQuery({
      queryKey: getMakeupSessionsQueryKey({
        page,
        pageSize: MAKEUP_SESSIONS_PAGE_SIZE,
        lastSelectedId,
        filters: apiFilters,
      }),
      queryFn: () =>
        searchMakeupSessions(accessToken, {
          page,
          pageSize: MAKEUP_SESSIONS_PAGE_SIZE,
          lastSelectedId,
          filters: apiFilters,
        }),
    }),
    queryClient.prefetchQuery({
      queryKey: [QueryKeys.MakeupEligibilitySettings, tenantId],
      queryFn: () =>
        fetchMakeupsEligibilitySettings(tenantId, { accessToken }),
      staleTime: 60_000,
    }),
  ]);

  return queryClient;
}
